import { useState, useEffect, useRef } from 'react';

interface OnlineStatus {
  isOnline: boolean;
  /** Briefly `true` for one render cycle when the browser transitions from
   * offline → online. Use this to trigger a "back online" toast. */
  justReconnected: boolean;
}

/**
 * Returns real-time network status.
 * `isOnline`        — current online state.
 * `justReconnected` — pulses `true` for one tick when we come back online.
 */
export function useOnlineStatus(): OnlineStatus {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [justReconnected, setJustReconnected] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      setJustReconnected(true);
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => setJustReconnected(false), 200);
    };
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  return { isOnline, justReconnected };
}
